import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import TableSkeleton from "./TableSkeleton";
import ColumnPicker from "./ColumnPicker";
import useColumnasVisibles from "./useColumnasVisibles";
import "./DataTable.css";
import "./GroupedTable.css";

export default function GroupedTable({
  data = [],
  groupField,
  loading = false,
  tableKey,
  columns,
  aggregates = [],
  // Devuelve las filas que cuelgan de una fila. Si trae algo, la fila se puede
  // abrir para verlas debajo, con sangría y sin salir de la tabla.
  subRows,
  // Alta en el propio renglón: `borradorEn` dice debajo de qué fila va el
  // formulario y `renderBorrador` lo dibuja.
  borradorEn,
  renderBorrador,
  // Renglón en blanco al principio de la tabla, para dar de alta algo que no
  // cuelga de ninguna fila (una cuenta nueva).
  borradorArriba,
  // Se llama con las filas hermanas ya reacomodadas y con el nivel en el que
  // se soltó. Si no viene, los renglones no se pueden arrastrar.
  onReordenar,
  noRecordsText = "No hay información disponible",
  formatCurrency = (value) => value,
}) {
  // En el primer render el hook de la pantalla todavía reporta loading:false
  // (lo pone en true dentro de su efecto de montaje), y sin esto se veía un
  // parpadeo de "no hay información" antes de las filas fantasma.
  const [montando, setMontando] = useState(true);

  useEffect(() => {
    const id = window.setTimeout(() => setMontando(false), 0);
    return () => window.clearTimeout(id);
  }, []);

  const mostrarSkeleton = loading || (montando && data.length === 0);

  // Columnas que este usuario decidió ver en esta tabla.
  const { visibles, columnasVisibles, alternar, mostrarTodas } =
    useColumnasVisibles(tableKey, columns);

  const [sort, setSort] = useState({ field: null, dir: null });
  const [busqueda, setBusqueda] = useState("");
  // Los grupos arrancan cerrados: primero se ve el total de cada etapa y el
  // total general, y se abre sólo lo que se quiere revisar.
  const [collapsed, setCollapsed] = useState(new Set());
  const gruposIniciados = useRef(false);
  // Fila que se está arrastrando: de qué grupo de hermanas viene y en qué
  // posición estaba. Sólo se puede soltar entre sus propias hermanas.
  const arrastrando = useRef(null);
  // Orden con el que quedaron las filas al arrastrarlas, por familia de
  // hermanas. Se aplica en el momento para que el renglón se mueva solo, sin
  // volver a pedir todo el presupuesto y parpadear la tabla entera.
  const [ordenLocal, setOrdenLocal] = useState({});
  // Qué filas están abiertas mostrando sus subcuentas. La llave es el camino
  // ("3-0-2") y no el id, porque los ids se repiten entre niveles.
  const [abiertas, setAbiertas] = useState(new Set());

  // Un solo buscador para toda la tabla (ver DataTable).
  const filtered = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return data;

    const buscables = columns.filter((col) => col.filterable !== false);

    return data.filter((row) =>
      buscables.some((col) => {
        const cell = col.filterValue ? col.filterValue(row) : row[col.field];
        return String(cell ?? "")
          .toLowerCase()
          .includes(termino);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, busqueda]);

  const groups = useMemo(() => {
    const sortRows = (rows) => {
      if (!sort.field) return rows;
      const col = columns.find((c) => c.field === sort.field);
      const getValue = (col && col.sortValue) || ((row) => row[sort.field]);
      const arr = [...rows].sort((a, b) => {
        const av = getValue(a);
        const bv = getValue(b);
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return av - bv;
        return String(av).localeCompare(String(bv), "es", { numeric: true });
      });
      return sort.dir === "desc" ? arr.reverse() : arr;
    };

    const map = new Map();
    filtered.forEach((row) => {
      const key = row[groupField] ?? "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });

    return Array.from(map.entries()).map(([key, rows]) => {
      const totals = {};
      aggregates.forEach((agg) => {
        totals[agg.field] = rows.reduce(
          (sum, r) => sum + (parseFloat(r[agg.field]) || 0),
          0
        );
      });
      return { key, rows: sortRows(rows), totals };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort]);

  // La primera vez que llegan los datos se cierran todos los grupos. Después
  // ya no se toca: lo que el usuario abra se queda abierto.
  useEffect(() => {
    if (gruposIniciados.current || groups.length === 0) return;
    gruposIniciados.current = true;
    setCollapsed(new Set(groups.map((grupo) => grupo.key)));
  }, [groups]);

  // Total de todo lo que se está viendo, no sólo de un grupo.
  const totalGeneral = useMemo(() => {
    const totales = {};

    aggregates.forEach((agg) => {
      totales[agg.field] = groups.reduce(
        (suma, grupo) => suma + (grupo.totals[agg.field] || 0),
        0
      );
    });

    return totales;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Desde qué columna empiezan los importes: antes de esa va el nombre del
  // grupo y después, cada total debajo de su columna.
  const primeraColumnaConTotal = useMemo(() => {
    const indice = columnasVisibles.findIndex((col) =>
      aggregates.some((agg) => agg.field === col.field)
    );

    return indice === -1 ? columnasVisibles.length : indice;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnasVisibles]);

  /** Celdas de importe alineadas bajo su columna. */
  const celdasDeTotales = (totales) =>
    columnasVisibles.slice(primeraColumnaConTotal).map((col) => {
      const agg = aggregates.find((a) => a.field === col.field);

      return (
        <td key={col.field} className="gtable-total-celda">
          {agg ? <strong>{formatCurrency(totales[agg.field])}</strong> : null}
        </td>
      );
    });

  const toggleSort = (field) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, dir: "asc" };
      if (prev.dir === "asc") return { field, dir: "desc" };
      return { field: null, dir: null };
    });
  };

  const alternarFila = (clave) => {
    setAbiertas((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });
  };

  /**
   * Una fila y, si está abierta, las que cuelgan de ella.
   *
   * El nivel 0 son las cuentas y usa `render`; de ahí para abajo son
   * subcuentas y usan `renderSub`. Una columna sin `renderSub` (los botones
   * que sólo aplican a la cuenta) sale vacía en las subfilas.
   */
  /** Filas de una familia acomodadas como quedaron al arrastrar. */
  const conOrdenLocal = (filas, familia) => {
    const orden = ordenLocal[familia];
    if (!orden) return filas;

    const porId = new Map(filas.map((fila) => [fila.id, fila]));
    const acomodadas = orden.map((id) => porId.get(id)).filter(Boolean);
    // Lo que llegó después de reordenar se va al final.
    const nuevas = filas.filter((fila) => !orden.includes(fila.id));

    return acomodadas.length ? [...acomodadas, ...nuevas] : filas;
  };

  const soltarEn = (hermanas, indiceDestino, familia) => {
    const origen = arrastrando.current;
    arrastrando.current = null;

    if (!origen || origen.familia !== familia) return;
    if (origen.indice === indiceDestino) return;

    const acomodadas = [...hermanas];
    const [movida] = acomodadas.splice(origen.indice, 1);
    acomodadas.splice(indiceDestino, 0, movida);

    setOrdenLocal((actual) => ({
      ...actual,
      [familia]: acomodadas.map((fila) => fila.id),
    }));

    onReordenar(acomodadas, origen.nivel);
  };

  const renderFila = (row, clave, nivel, hermanas = [], indice = 0, familia = "") => {
    const hijas = conOrdenLocal((subRows && subRows(row)) || [], clave);
    const abierta = abiertas.has(clave);
    const sePuedeArrastrar = !!onReordenar;
    // La fila que disparó el formulario de alta se marca igual que el
    // formulario (mismo acento verde), para que se vea como un solo bloque:
    // antes sólo el renglón del formulario cambiaba de color y, si había
    // varias filas abiertas o se hacía scroll, no quedaba claro en cuál
    // cuenta se estaba dando de alta algo.
    const tieneBorradorAbierto = !!(borradorEn && borradorEn(row, nivel));
    const clases = [
      nivel > 0 ? "gtable-subfila" : null,
      tieneBorradorAbierto ? "gtable-fila-activa" : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

    return (
      <Fragment key={clave}>
        <tr
          className={clases}
          draggable={sePuedeArrastrar}
          onDragStart={() => {
            arrastrando.current = { familia, indice, nivel };
          }}
          onDragOver={(evento) => {
            if (arrastrando.current?.familia === familia) evento.preventDefault();
          }}
          onDrop={(evento) => {
            evento.preventDefault();
            soltarEn(hermanas, indice, familia);
          }}
        >
          {columnasVisibles.map((col, posicion) => {
            const contenido =
              nivel === 0
                ? col.render
                  ? col.render(row)
                  : row[col.field]
                : col.renderSub
                ? col.renderSub(row, nivel)
                : null;

            return (
              <td
                key={col.field}
                className={col.align === "center" ? "text-center" : undefined}
              >
                {posicion === 0 ? (
                  <span
                    className="gtable-celda-arbol"
                    style={{ paddingLeft: `${nivel * 1.1}rem` }}
                  >
                    {sePuedeArrastrar && (
                      <span className="gtable-tirador" title="Arrastra para reordenar">
                        <i className="bi bi-grip-vertical" aria-hidden="true"></i>
                      </span>
                    )}
                    {hijas.length > 0 ? (
                      <button
                        type="button"
                        className="gtable-expandir"
                        onClick={() => alternarFila(clave)}
                        aria-expanded={abierta}
                        aria-label={
                          abierta ? "Ocultar subcuentas" : "Ver subcuentas"
                        }
                      >
                        <i
                          className={`bi ${
                            abierta ? "bi-dash-square" : "bi-plus-square"
                          }`}
                        ></i>
                      </button>
                    ) : (
                      <span className="gtable-expandir-hueco" />
                    )}
                    {contenido}
                  </span>
                ) : (
                  contenido
                )}
              </td>
            );
          })}
        </tr>
        {tieneBorradorAbierto && (
          <tr className="gtable-borrador">
            <td colSpan={columnasVisibles.length}>
              {renderBorrador(row, nivel)}
            </td>
          </tr>
        )}
        {abierta &&
          hijas.map((hija, i) =>
            renderFila(hija, `${clave}-${i}`, nivel + 1, hijas, i, clave)
          )}
      </Fragment>
    );
  };

  const toggleGroup = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="dtable-wrap">
      <div className="dtable-toolbar">
        {tableKey && (
          <ColumnPicker
            columns={columns}
            visibles={visibles}
            onToggle={alternar}
            onMostrarTodas={mostrarTodas}
          />
        )}
        <div className="dtable-search">
          <i className="bi bi-search" aria-hidden="true"></i>
          <input
            type="search"
            className="dtable-search-input"
            placeholder="Buscar en la tabla..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar en la tabla"
          />
        </div>
      </div>
      <div className="dtable-scroll">
        <table className="dtable gtable">
          <thead>
            <tr>
              {columnasVisibles.map((col) => (
                <th
                  key={col.field}
                  style={{ width: col.width }}
                  className={col.align === "center" ? "text-center" : undefined}
                >
                  {col.sortable === false ? (
                    col.title
                  ) : (
                    <button
                      type="button"
                      className="dtable-sort-btn"
                      onClick={() => toggleSort(col.field)}
                    >
                      {col.title}
                      <i
                        className={`bi ${
                          sort.field === col.field
                            ? sort.dir === "asc"
                              ? "bi-arrow-up"
                              : "bi-arrow-down"
                            : "bi-arrow-down-up"
                        }`}
                      ></i>
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {borradorArriba && (
              <tr className="gtable-borrador">
                <td colSpan={columnasVisibles.length}>{borradorArriba}</td>
              </tr>
            )}
            {mostrarSkeleton ? (
              <TableSkeleton columnas={columnasVisibles} />
            ) : groups.length === 0 ? (
              <tr>
                <td colSpan={columnasVisibles.length} className="dtable-empty">
                  {noRecordsText}
                </td>
              </tr>
            ) : (
              groups.map((group) => (
                <Fragment key={group.key}>
                  <tr className="gtable-group-row">
                    {/* Los totales van aquí y no en un pie: así se ven también
                        con el grupo cerrado, que es cuando más se necesitan. */}
                    <td colSpan={Math.max(1, primeraColumnaConTotal)}>
                      <button
                        type="button"
                        className="gtable-group-toggle"
                        onClick={() => toggleGroup(group.key)}
                        aria-expanded={!collapsed.has(group.key)}
                      >
                        <i
                          className={`bi ${
                            collapsed.has(group.key)
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                        ></i>
                        {group.key}
                        <span className="gtable-group-cuenta">
                          {group.rows.length}
                        </span>
                      </button>
                    </td>
                    {celdasDeTotales(group.totals)}
                  </tr>
                  {!collapsed.has(group.key) &&
                    conOrdenLocal(group.rows, `grupo:${group.key}`).map((row, idx) =>
                      renderFila(
                        row,
                        `${group.key}-${row.id ?? idx}`,
                        0,
                        conOrdenLocal(group.rows, `grupo:${group.key}`),
                        idx,
                        `grupo:${group.key}`
                      )
                    )}
                </Fragment>
              ))
            )}

            {!mostrarSkeleton && groups.length > 0 && aggregates.length > 0 && (
              <tr className="gtable-total-general">
                <td colSpan={Math.max(1, primeraColumnaConTotal)}>
                  Total general
                </td>
                {celdasDeTotales(totalGeneral)}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
